import { ChevronRightIcon } from "assets";
import classNames from "classnames";
import { Account } from "domain/accounts";
import { useAuth } from "domain/authentication";
import { User, UserPermissions, UserType } from "domain/users";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLoading } from "shared/hooks";
import styles from "./dashboard.page.module.scss";
import { OptionLink, getOptions } from "./options";
import { jwtDecode } from "jwt-decode";
import { useCookies } from "react-cookie";
import { COOKIE_NAME } from "data/constants";

export const DashboardPage = () => {
  const {
    user,
    availableAccounts,
    defaultAccount,
    permissions,
    getPermissions: getPermissionsRequest,
    logOut
  } = useAuth();

  const [getPermissions, isLoading] = useLoading(getPermissionsRequest);
  const [cookies] = useCookies([COOKIE_NAME]);
  const [selectedAccount, setSelectedAccount] = useState(defaultAccount);

  const shouldDisplayAccountOption = useMemo(
    () => user?.userType !== UserType.QuikQuoteUser,
    [user]
  );

  useEffect(() => {
    const token = cookies.userToken;
    if (!token || !user || !selectedAccount || !availableAccounts || !permissions)
      {
        logOut() ;
        return;
      } 

    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    if (decodedToken.exp && (decodedToken.exp < currentTime)) {
      logOut();
    }

  }, [cookies, user, selectedAccount, permissions, availableAccounts]);

  if (
    !cookies.userToken ||
    !user ||
    !selectedAccount ||
    !availableAccounts ||
    !permissions
  ) {
    return null;
  }

  const handleSelectItemClick = (userId: number, accountId: number) => {
    getPermissions(
      {
        userId,
        accountId,
      },
      {
        onSuccess: () => {
          setSelectedAccount(
            availableAccounts.find((acc) => acc.accountID === accountId)
          );
        },
      }
    );
  };

  return (
    <div className="bg-lighter-gray flex-1">
      <div className={classNames("container", styles.content)}>
        <h1 className={styles.title}>Welcome back!</h1>
        {shouldDisplayAccountOption && (
          <AccountSelector
            selectedId={selectedAccount.accountID}
            onChange={handleSelectItemClick}
            accounts={availableAccounts}
            userId={user.userID}
          />
        )}
        <OptionsGrid
          user={user}
          accountId={selectedAccount.accountID}
          permissions={permissions}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

interface OptionsGridProps {
  user: User;
  accountId: number;
  permissions: UserPermissions;
  isLoading: boolean;
}

const OptionsGrid = ({
  user,
  accountId,
  permissions,
  isLoading,
}: OptionsGridProps) => {
  const [selectedOption, setSelectedOption] = useState<string>();

  useEffect(() => {
    setSelectedOption(undefined);
  }, [isLoading]);

  const options = getOptions(user, accountId, permissions);

  const handleItemClick = (key: string) =>
    setSelectedOption((prev) => (prev === key ? undefined : key));

  return (
    <div className={styles.grid}>
      {options.map(({ icon, links, title }) => (
        <GridItem
          key={title}
          icon={icon}
          title={title}
          links={links}
          onClick={handleItemClick}
          isSelected={selectedOption === title}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
};

interface GridItemProps {
  icon: React.ReactNode;
  title: string;
  links: OptionLink[];
  onClick: (key: string) => void;
  isSelected: boolean;
  isLoading: boolean;
}

const GridItem = ({
  icon,
  title,
  links,
  onClick,
  isSelected,
  isLoading
}: GridItemProps) => (
  <div
    className={classNames(
      styles.item,
      isSelected && styles.active,
      isLoading && styles.disabled
    )}
  >
    <div className={styles.tile} onClick={() => !isLoading && onClick(title)}>
      <div className={styles.icon}>{icon}</div>
      <span className={styles.label}>{title}</span>
      <div className={styles.indicator}>
        <ChevronRightIcon />
      </div>
    </div>
    <div className={styles.links}>
      {links.map((link) => (
        <GridItemLink key={`${link.url}${link.label}`} link={link} />
      ))}
    </div>
  </div>
);

interface GridItemLinkProps {
  link: OptionLink;
}

const GridItemLink = ({ link }: GridItemLinkProps) => {

  const formRef = useRef<HTMLFormElement>(null);
 
  const handleRedirect = () => {
    window.location.href = `${link.url}`;
  };
 
  return (
  <div className={styles.link}>
    <div onClick={handleRedirect}>
      <span>{link.label}</span>
      <ChevronRightIcon />
    </div>
  </div>


);
}

interface AccountSelectorProps {
  accounts: Account[];
  selectedId: number;
  userId: number;
  onChange: (userId: number, accountId: number) => void;
}

const AccountSelector = ({
  accounts,
  selectedId,
  userId,
  onChange,
}: AccountSelectorProps) => (
  <div className={styles.accounts}>
    <h5 className={styles.label}>Select the account</h5>

    <div className={styles.select}>
      <select onChange={(e) => onChange(userId, +e.target.value)} value={selectedId}>
        {accounts.map(({ accountID, name, accountKey }) => (
          <option key={accountKey} value={accountID}>
            {name}
          </option>
        ))}
      </select>
    </div>
  </div>
);


